import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { HorizontalScrollContainer } from '../../../components/horizontal-scroll-container/HorizontalScrollContainer';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import Pagination from '../../../components/pagination/Pagination';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import ViewToggle from '../../../components/view-toggle/ViewToggle';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePagination } from '../../../hooks/usePagination';
import { postOfficeHoursList } from '../../../services/practices/postOfficeHoursList';
import { getProvidersList } from '../../../services/providers/getProvidersList';
import {
  OfficeHour,
  OfficeHoursApiResponse,
  Provider,
  ProvidersResponse,
} from '../../../types';
import { applyExternalFilterForNonAdmin } from '../../../utils/externalEntity';
import ProviderCompactCard from '../providers/ProviderCompactCard';
import { createProviderColumns } from '../providers/ProvidersColumns';

interface GroupedOfficeHours {
  [practice_id: string]: OfficeHour[];
}

interface MarketProviderListProps {
  marketId?: string[];
  practiceIds?: string[];
  providerIds?: string[];
  insuranceIds?: string[];
  state?: string;
  name?: string;
}
interface FilterParams {
  [key: string]: string | string[] | undefined;
  marketId?: string[];
  practiceIds?: string[];
  providerIds?: string[];
  insuranceIds?: string[];
  state?: string;
}

const MarketProviderList: React.FC<MarketProviderListProps> = ({
  marketId,
  practiceIds,
  providerIds,
  insuranceIds,
  state,
  name,
}) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<
    'lastName' | 'firstName' | 'status'
  >('lastName');
  const [sortOrder, setSortOrder] = useState<'ascending' | 'descending'>(
    'ascending'
  );
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandAll, setExpandAll] = useState(false);
  const [duplicateProviders] = useState<Record<string, string>>({});
  const { permissions, isReady } = usePermissions();

  const filters = useMemo<FilterParams>(() => {
    const groupByStatus = sortField === 'status' ? 'true' : 'false';
    const lastNameSort =
      sortField === 'firstName' ? 'false' : 'true';
    const sortOrderAsc =
      sortOrder === 'ascending' ? 'true' : 'false';

    return {
      marketId,
      practiceIds,
      providerIds,
      insuranceIds,
      state,
      lastNameSort,
      sortOrderAsc,
      groupByStatus,
    };
  }, [marketId, practiceIds, providerIds, insuranceIds, state, sortField, sortOrder]);

  // Create stable fetch function for pagination
  const fetchProviders = useCallback(
    (startToken: string | null) =>
      getProvidersList(
        startToken,
        applyExternalFilterForNonAdmin(filters, permissions.isAdmin),
        10
      ),
    [filters, permissions.isAdmin]
  );

  // Use pagination hook for efficient data loading
  const {
    items: providers,
    loading,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
    setItems: setProviders,
  } = usePagination<Provider, ProvidersResponse>(
    fetchProviders,
    10,
    { useClientSidePagination: false },
    filters,
    isReady
  );

  // Note: We don't update the parent count here because Market Detail tabs
  // should always show the true total from market data, not the current page count
  const [officeHoursLoading, setOfficeHoursLoading] = useState(false);
  const [officeHours, setOfficeHours] = useState<GroupedOfficeHours>(
    {}
  );
  const fetchedOfficeHoursRef = useRef<Set<string>>(new Set());
  const toast = useToast();
  const [isDataFetched, setIsDataFetched] = useState(false);

  // Sorting is handled server-side (see `filters` above), so this only
  // needs to apply the local search filter - re-sorting client-side would
  // only reorder the current page and break the server's ordering across
  // pages.
  const filteredAndSortedProviders = useMemo(() => {
    if (!searchQuery.trim()) return providers;

    const query = searchQuery.toLowerCase();
    return providers.filter((provider) => {
      const firstName = provider.firstname?.toLowerCase() || '';
      const lastName = provider.lastname?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      const specialty = provider.specialty?.toLowerCase() || '';
      const npi = provider.npi?.toLowerCase() || '';
      return (
        fullName.includes(query) ||
        specialty.includes(query) ||
        npi.includes(query)
      );
    });
  }, [providers, searchQuery]);

  useEffect(() => {
    if (!loading && providers.length >= 0) {
      setIsDataFetched(true);
    }
  }, [loading, providers, setIsDataFetched]);

  const groupByPracticeId = useCallback(
    (response: OfficeHoursApiResponse): GroupedOfficeHours => {
      return response.office_hours.reduce(
        (acc: GroupedOfficeHours, curr: OfficeHour) => {
          const key = curr.practice_id;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(curr);
          return acc;
        },
        {}
      );
    },
    []
  );

  const fetchOfficeHours = useCallback(
    async (practiceIds: string[]) => {
      try {
        setOfficeHoursLoading(true);
        const data = await postOfficeHoursList(practiceIds);
        const groupedOfficeHours = groupByPracticeId(data.data);
        setOfficeHours(groupedOfficeHours);
        fetchedOfficeHoursRef.current = new Set([
          ...fetchedOfficeHoursRef.current,
          ...practiceIds,
        ]);
      } catch (error) {
        toast({
          title: 'Failed to fetch office hours',
          description: `Please try again`,
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setOfficeHoursLoading(false);
      }
    },
    [groupByPracticeId, toast]
  );

  const updateProvidersWithOfficeHours = useCallback(() => {
    setProviders((prevItems) => {
      // Create a new array to avoid mutations
      const updatedProviders = prevItems.map((provider) => ({
        ...provider,
        relatedPractices: provider.relatedPractices
          ? provider.relatedPractices.map((practice) => ({
              ...practice,
              office_hours: officeHours[practice.id] || [],
            }))
          : [],
      }));

      // Only update if there are actual changes to avoid unnecessary re-renders
      const hasChanges = updatedProviders.some((provider, index) => {
        const originalProvider = prevItems[index];
        return provider.relatedPractices?.some(
          (practice, practiceIndex) => {
            const originalPractice =
              originalProvider.relatedPractices?.[practiceIndex];
            return (
              practice.office_hours !== originalPractice?.office_hours
            );
          }
        );
      });

      return hasChanges ? updatedProviders : prevItems;
    });
  }, [officeHours, setProviders]);

  useEffect(() => {
    const practiceIds = providers
      .flatMap(
        (provider) =>
          provider.relatedPractices?.map((practice) => practice.id) ||
          []
      )
      .filter((id): id is string => id !== undefined);

    const newPracticeIds = practiceIds.filter(
      (id) => !fetchedOfficeHoursRef.current.has(id)
    );

    if (newPracticeIds.length > 0 && !officeHoursLoading) {
      fetchOfficeHours(newPracticeIds);
    }
  }, [providers, officeHoursLoading, fetchOfficeHours]);

  useEffect(() => {
    if (Object.keys(officeHours).length > 0) {
      updateProvidersWithOfficeHours();
    }
  }, [officeHours, updateProvidersWithOfficeHours]);

  if (!marketId || marketId.length === 0) return null;

  if (loading) {
    return (
      <Box ml="2rem" mr="2rem" mb="3rem" mt="1.75rem">
        <PageLoader height="45vh" />
      </Box>
    );
  }
  // Don't show empty state if we're just filtering results
  const showEmptyState = isDataFetched && providers.length === 0;
  const showNoSearchResults =
    filteredAndSortedProviders.length === 0 && searchQuery.trim();

  return (
    <Box mb="3rem" mt="1.75rem">
      <Box>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          mb="1rem"
        >
          <Text
            fontSize="1.25rem"
            fontWeight="700"
            lineHeight="120%"
            color="text.secondary"
          >
            {name} Providers
          </Text>
          <Flex gap="0.25rem" alignItems="flex-end">
            <InputGroup width="18.75rem">
              <InputLeftElement
                pointerEvents="none"
                h="2.5rem"
                pl="1rem"
              >
                <SearchIcon color="gray.500" boxSize="1rem" />
              </InputLeftElement>
              <Input
                placeholder="Search providers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="#F7FAFC"
                borderColor="transparent"
                height="2.5rem"
                fontSize="0.875rem"
                pl="2.5rem"
                pr="1rem"
                borderRadius="0.375rem"
                _placeholder={{ color: 'gray.500' }}
                _focus={{
                  borderColor: 'blue.500',
                  boxShadow: '0 0 0 1px #3182ce',
                  bg: 'white',
                }}
                _hover={{
                  borderColor: 'gray.300',
                  bg: 'white',
                }}
              />
            </InputGroup>
            <Flex direction="column" justifyContent="flex-end" height="2.5rem">
              <Text
                fontSize="0.75rem"
                color="gray.600"
                mb="0.25rem"
                fontWeight="500"
                lineHeight="1"
              >
                Sort by
              </Text>
              <Select
                bg="#F7FAFC"
                width="12rem"
                height="2.5rem"
                fontSize="0.875rem"
                _hover={{ bg: 'white' }}
                _active={{
                  transform: 'scale(0.98)',
                }}
                _focus={{ boxShadow: 'outline' }}
                borderRadius="0.375rem"
                border="1px solid transparent"
                value={`${sortField}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-') as [
                    'lastName' | 'firstName' | 'status',
                    'ascending' | 'descending'
                  ];
                  setSortField(field);
                  setSortOrder(order);
                }}
              >
                <option value="lastName-ascending">Last Name A - Z</option>
                <option value="lastName-descending">Last Name Z - A</option>
                <option value="firstName-ascending">First Name A - Z</option>
                <option value="firstName-descending">First Name Z - A</option>
                <option value="status-ascending">Status (On PAC first)</option>
              </Select>
            </Flex>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setExpandAll(!expandAll)}
              bg="#F7FAFC"
              _hover={{ bg: 'white' }}
              height="2.5rem"
              px="1rem"
              fontSize="0.875rem"
              fontWeight="500"
              borderColor="transparent"
              borderRadius="0.375rem"
              _active={{
                transform: 'scale(0.98)',
              }}
            >
              {expandAll ? 'Collapse All' : 'Expand All'}
            </Button>
            <ViewToggle
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
          </Flex>
        </Flex>

        {showEmptyState ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            minH="300px"
            textAlign="center"
            mt="2rem"
          >
            <Text
              fontSize="1.25rem"
              fontWeight="600"
              color="text.secondary"
              mb="0.5rem"
            >
              No Providers Found
            </Text>
            <Text
              fontSize="0.875rem"
              color="gray.600"
              mb="1.5rem"
              maxW="400px"
            >
              Use "Add/Remove Practices" - providers under current
              practices will show here.
            </Text>
            <Button
              colorScheme="blue"
              size="md"
              onClick={() =>
                navigate(`/markets/${marketId?.[0]}/practices`)
              }
              _hover={{ transform: 'translateY(-1px)' }}
              transition="all 0.2s ease-in-out"
            >
              Add/Remove Practices
            </Button>
          </Flex>
        ) : showNoSearchResults ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            minH="300px"
            textAlign="center"
            mt="2rem"
          >
            <Text
              fontSize="1.25rem"
              fontWeight="600"
              color="text.secondary"
              mb="0.5rem"
            >
              No providers match your search
            </Text>
            <Text
              fontSize="0.875rem"
              color="gray.600"
              mb="1.5rem"
              maxW="400px"
            >
              Try adjusting your search terms or clear the search to
              see all providers.
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSearchQuery('')}
              _hover={{ transform: 'translateY(-1px)' }}
              transition="all 0.2s ease-in-out"
            >
              Clear Search
            </Button>
          </Flex>
        ) : viewMode === 'table' ? (
          <ResourceGrid<Provider>
            data={filteredAndSortedProviders}
            columns={createProviderColumns(duplicateProviders)}
          />
        ) : (
          <HorizontalScrollContainer>
            {filteredAndSortedProviders.map((provider, index) => (
              <ProviderCompactCard
                provider={provider}
                key={`${provider.id}-${index}`}
                officeHoursLoading={officeHoursLoading}
                isExpanded={expandAll}
                duplicateProviders={duplicateProviders}
              />
            ))}
          </HorizontalScrollContainer>
        )}

        {/* Helper text showing current page range */}
        {!loading && totalCount > 0 && (
          <Text
            fontSize="0.875rem"
            color="gray.600"
            mb="1rem"
            textAlign="center"
          >
            Showing {(currentPage - 1) * 10 + 1}–
            {Math.min(currentPage * 10, totalCount)} of {totalCount}
            {totalPages > 1 &&
              ` (Page ${currentPage} of ${totalPages})`}
          </Text>
        )}

        {/* Pagination UI - Show when there are more than 10 providers or multiple pages */}
        {(totalPages > 1 || totalCount > 10) && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages || Math.ceil(totalCount / 10)}
            onPageChange={handlePageChange}
          />
        )}
      </Box>
    </Box>
  );
};

export default MarketProviderList;
